import { Dialog, makeStyles, useMediaQuery, useTheme } from "@material-ui/core";
import { Button } from "@resource/atlas";
import { atlasClose } from "@resource/atlas/icons";
import { useEffect, useState } from "react";
import useAnalytics from "react-hooks/useAnalytics";
import { ResourceCustomTheme } from "styles/config";
import urljoin from "url-join";

export enum SchedulerMode {
  Schedule = 0,
  Reschedule = 1,
  Cancel = 2,
}

const generateSchedulerLink = ({
  mode,
  slug,
  editHash,
  queryParams,
}: {
  mode: SchedulerMode;
  slug: string;
  queryParams: URLSearchParams;
  editHash?: string | null;
}): string => {
  let editHashParam = "";
  let method = "";

  switch (mode) {
    case SchedulerMode.Cancel:
      method = "cancel";
      editHashParam = editHash ?? "";
      break;
    case SchedulerMode.Reschedule:
      method = "reschedule";
      editHashParam = editHash ?? "";
      break;
    default:
      break;
  }

  return urljoin(
    "https://schedule.nylas.com",
    encodeURI(slug ?? ""),
    method,
    editHashParam,
    `?${queryParams.toString()}`
  );
};

const useSchedulerStyles = makeStyles((theme: ResourceCustomTheme) => ({
  root: {},
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  wrap: {
    padding: `0px ${theme.spacing(2)}px 0px`,
    width: "100%",
    height: "90vh",
    maxHeight: "720px",
  },
  iframe: {
    width: "100%",
    height: "100%",
    border: "none",
  },
}));

export type RedirectParams = {
  // eslint-disable-next-line camelcase
  edit_hash: string;
  stageId?: string;
  pitchPageId?: string;
  href?: string;
};

type NylasSchedulerProps = {
  slug: string;
  mode: SchedulerMode;
  editHash?: string;
  candidateEmail?: string;
  candidateName?: string;
  stageId?: string;
  pitchPageId?: string;
  open: boolean;
  onClose(): void;
  onRedirect(params: RedirectParams): void;
};

function NylasSchedulerModal({
  slug,
  mode,
  editHash: currentEditHash,
  candidateEmail,
  candidateName,
  stageId,
  pitchPageId,
  open,
  onClose,
  onRedirect,
}: NylasSchedulerProps) {
  const analytics = useAnalytics();
  const classes = useSchedulerStyles();
  const theme = useTheme<ResourceCustomTheme>();
  const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));
  const [editHash, setEditHash] = useState("");
  useEffect(() => {
    if (editHash !== currentEditHash) {
      setEditHash(currentEditHash ?? "");
    }
  }, [currentEditHash, editHash]);

  const url = generateSchedulerLink({
    slug,
    mode,
    editHash,
    queryParams: new URLSearchParams({
      ...(candidateEmail ? { email: candidateEmail } : {}),
      ...(candidateName ? { name: candidateName } : {}),
      ...(stageId ? { stageId } : {}),
      ...(pitchPageId ? { pitchPageId } : {}),
    }),
  });

  useEffect(() => {
    const iframeListener = (event: MessageEvent) => {
      if (event.data?.params?.fromScheduler === "true") {
        // Event was scheduled
        if (pitchPageId) {
          analytics.track("PP Scheduler Event Scheduled");
        } else {
          analytics.track("Scheduler Event Scheduled");
        }
        onRedirect(event.data.params);
      }
    };
    window.addEventListener("message", iframeListener);

    return () => {
      window.removeEventListener("message", iframeListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onRedirect]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={fullScreen}
    >
      <div className={classes.wrap}>
        <Button size="medium" icon={atlasClose} onClick={onClose} isGhost />
        <iframe
          id="calendar-widget"
          className={classes.iframe}
          title="calendar widget iframe"
          src={url}
        />
      </div>
    </Dialog>
  );
}

export default NylasSchedulerModal;
